import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AlertBox, TournamentPageSkeleton } from "../UI/DesignStandards";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import { SignupFilters, SignupList } from "./SignupDashboard/components";
import SignupTotalsStrip from "./SignupDashboard/components/SignupTotalsStrip";
import { useSignupData } from "./SignupDashboard/hooks/useSignupData";
import { useSignupFilters } from "./SignupDashboard/hooks/useSignupFilters";
import { directorSetSignupPayment } from "../../utility/signupRpc";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";

const SignupDashboard = () => {
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const navigate = useNavigate();
  const [actionId, setActionId] = useState(null);
  const [actionError, setActionError] = useState(null);

  const { signups, loading, error, refreshSignups } = useSignupData(tournamentId);

  const {
    showFilters,
    setShowFilters,
    filters,
    searchTerm,
    setSearchTerm,
    filteredSignups,
    handleFilterChange,
    clearFilters,
    setQuickFilter,
    removeFilter,
  } = useSignupFilters(signups);

  const handleViewSignup = (signup) => {
    navigate(tournamentPath(tournamentSlug, `/signup-dashboard/${signup.id}`));
  };

  const setPayment = async (signup, paymentStatus) => {
    try {
      setActionId(signup.id);
      setActionError(null);
      await directorSetSignupPayment({
        signupId: signup.id,
        paymentStatus,
      });
      await refreshSignups({ silent: true });
    } catch (paymentError) {
      setActionError(
        paymentError.message || "Could not update payment."
      );
    } finally {
      setActionId(null);
    }
  };

  if (loading) {
    return (
      <TournamentPageSkeleton
        title="Signup Dashboard"
        subtitle="Squads, waitlist, and who still owes."
        cards={3}
      />
    );
  }

  if (error) {
    return <AlertBox type="error" title="Error" message={error} />;
  }

  return (
    <>
      <div className="flex justify-center mb-3 sm:mb-4 px-4 sm:px-0">
        <Breadcrumb
          items={[
            { label: "Tournaments", path: "/tournaments" },
            {
              label: "Tournament",
              path: tournamentPath(tournamentSlug),
            },
            { label: "Signup Dashboard" },
          ]}
        />
      </div>

      <div className="space-y-6">
        <div className="card-board overflow-hidden">
          <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Signup Dashboard
            </h1>
            <p className="text-sm text-brand-mist mt-1">
              Squads, waitlist, and who still owes.
            </p>
          </div>
          <div className="px-4 sm:px-6 pt-4 pb-4">
            <SignupTotalsStrip
              signups={signups}
              tournamentId={tournamentId}
            />
          </div>
          <div className="p-4 sm:p-6 pt-0">
            {actionError ? (
              <div className="mb-4">
                <AlertBox type="error" title="Error" message={actionError} />
              </div>
            ) : null}
            <SignupFilters
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              filters={filters}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              signups={signups}
              clearFilters={clearFilters}
              setQuickFilter={setQuickFilter}
              removeFilter={removeFilter}
              handleFilterChange={handleFilterChange}
            />
          </div>
        </div>

        <SignupList
          signups={filteredSignups}
          allSignups={signups}
          actionId={actionId}
          onViewSignup={handleViewSignup}
          onMarkPaid={(signup) => setPayment(signup, "walk_up")}
          onMarkUnpaid={(signup) => setPayment(signup, "unpaid")}
        />
      </div>
    </>
  );
};

export default SignupDashboard;
